import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { getIdMap } from 'ontraccr-common/lib/Common';

import Permissions from '../../../../auth/Permissions';
import useToggle from '../../../../common/hooks/useToggle';
import { INVOICE_DRAWER_ADD_MODE, INVOICE_DRAWER_VIEW_MODE } from '../../../../payables/invoices/invoiceConstants';
import InvoiceAddDrawer from '../../../../payables/invoices/InvoiceAddDrawer';
import { getInvoices } from '../../../../payables/invoices/state/invoices.actions';
import { getProgress } from '../../../../reports/state/reports.actions';
import { getCompanyImageURL } from '../../../../settings/state/settings.actions';
import {
  applySubcontractSummary,
  areFiltersEqual,
  buildCategorizedCostUpdateMap,
  computeActualTotalSpend,
  enrichRowsWithCostUpdates,
  getActualCostForCostCode,
  getActualSpendForCategory,
  getProgressFromState,
  processUncategorizedInvoices,
  processUncategorizedUpdates,
} from '../../../ProjectProgress/progressHelpers';
import ProgressHistoryDrawer from '../../../ProjectProgress/ProgressHistoryDrawer';
import { scheduleOfValuesData } from '../../../ProjectScheduleOfValues/helpers';
import {
  getAllProjectCostcodeDetails,
  getProgressSubContractUpdates,
  getProjectScheduleOfValues,
  getScheduleOfValueSections,
  getSubContracts,
  updateProjectProgressFilters,
} from '../../../state/projects.actions';
import { getProjectIdMap, getUserIdMap } from '../../../../state/selectors';
import useProjectCostcodesFetch from '../../../../costcodes/useProjectCostcodesFetch';
import {
  attachCostcodeCosts,
  buildCostcodesTableData,
  getBaseSummary,
  getBudgetUpdateEstimateTotals,
  getSummaryAllocatedHours,
  getSummaryOverBudgetItems,
  getSummaryPhasedData,
  getSummaryUncategorizedCosts,
} from '../../budgetTrackingHelpers';
import CostTrackingAnalysis from './CostTrackingAnalysis';
import CostTrackingBudget from './CostTrackingBudget';
import CostTrackingEstimates from './CostTrackingEstimates';
import CostTrackingSubcontracts from './CostTrackingSubcontracts';
import CostTrackingSummaryOverview from './CostTrackingSummaryOverview';
import CostTrackingUncategorized from './CostTrackingUncategorized';
import CostTrackingNavigation from './CostTrackingNavigation';

const dataTimers = {
  progress: {
    time: DateTime.fromObject({ year: 1900 }),
    filters: {},
  },
  subcontractorsAndCostcodeDetails: {
    time: DateTime.fromObject({ year: 1900 }),
    filters: {},
  },
};

// Budget rows display/edit these, and progress history entries display the billing rates.
const COSTCODE_COLUMNS = [
  'description',
  'hourlyWage',
  'dailyWage',
  'hourlyBillingRate',
  'dailyBillingRate',
];

const SUMMARY_KEY_BY_CATEGORY_NAME = {
  Material: 'actualMaterialSpend',
  Equipment: 'actualEquipmentSpend',
  Overhead: 'actualOverheadSpend',
  Labor: 'actualLaborSpend',
};

// Add default category + custom category values into the summary.
const addSummaryValues = ({
  row,
  summary,
  estimatedBurdenRate,
}) => {
  /* eslint-disable no-param-reassign -- intentional accumulation into summary */
  const {
    categoryName,
    categoryId: rowCategoryId,
    actualCost: rowActualCost,
    totalCost: rowTotalCost, // actualSpend + actualCost (cost only applies for time tracking)
  } = row;
  const actualCost = rowActualCost ?? 0;
  const totalCost = rowTotalCost ?? 0;
  const summaryKey = SUMMARY_KEY_BY_CATEGORY_NAME[categoryName];
  if (summaryKey) {
    summary[summaryKey] += totalCost;
    if (categoryName === 'Labor') {
      summary.actualBurdenCost += actualCost * estimatedBurdenRate;
    }
    return;
  }
  if (rowCategoryId != null) {
    summary[rowCategoryId] = (summary[rowCategoryId] ?? 0) + totalCost;
  }
  /* eslint-enable no-param-reassign */
};

const flattenRows = (rows) => rows.flatMap((row) => (
  row.isParentRow && row.children?.length ? row.children : [row]
));

export default function CostTracking({
  visible,
  projectId,
  divisionId,
  templateId,
}) {
  const dispatch = useDispatch();

  const hasPOPerms = Permissions.match('PURCHASE_ORDERS');
  const hasWrite = Permissions.has('PROJECT_PROGRESS_WRITE');

  const { phases: statePhases = [] } = useSelector((state) => state.costcodes);
  const {
    projectCostcodeDetails: projectCostcodeDetailsMap,
    projectProgressFilters,
  } = useSelector((state) => state.projects);
  const progresses = useSelector((state) => state.reports.progress);
  const invoices = useSelector((state) => state.invoices.invoices);
  const statuses = useSelector((state) => state.invoices.statuses);
  const vendors = useSelector((state) => state.vendors.vendors);
  const categoryAssignments = useSelector((state) => state.costcodes.categoryAssignments);
  const categoriesIdMap = useSelector((state) => state.costcodes.categoriesIdMap);
  const projectCostUpdates = useSelector((state) => state.projects.projectCostUpdates);
  const customCategoryEstimates = useSelector((state) => state.projects.customCategoryEstimates);
  const scheduleOfValueSections = useSelector((state) => state.projects.scheduleOfValueSections);
  const scheduleOfValues = useSelector((state) => state.projects.scheduleOfValues);

  const costcodeProjectIds = useMemo(() => (projectId ? [projectId] : []), [projectId]);

  // Archived cost codes are included: they can still carry actual spend on this project.
  const {
    projectCostcodes: fetchedProjectCostcodes,
    refresh: refreshProjectCostcodes,
  } = useProjectCostcodesFetch({
    projectIds: costcodeProjectIds,
    includeCategoryIds: true,
    columns: COSTCODE_COLUMNS,
    active: false,
  });

  const projectCostcodes = useMemo(() => fetchedProjectCostcodes.map((costcode) => {
    const { id: costcodeId } = costcode;
    const assignments = categoryAssignments?.[costcodeId]?.unphased ?? {};
    return {
      ...costcode,
      categoryAssignments: assignments,
    };
  }), [fetchedProjectCostcodes, categoryAssignments]);

  const phases = useMemo(() => statePhases.map((phase) => {
    const { id: phaseId, costcodeId } = phase;
    const assignments = categoryAssignments?.[costcodeId]?.[phaseId] ?? {};
    return {
      ...phase,
      categoryAssignments: assignments,
    };
  }), [statePhases, categoryAssignments]);

  // maps
  const projectMap = useSelector(getProjectIdMap);
  const userMap = useSelector(getUserIdMap);
  const costcodeIdMap = useMemo(() => getIdMap(projectCostcodes), [projectCostcodes]);
  const invoiceMap = useMemo(() => getIdMap(invoices), [invoices]);
  const statusMap = useMemo(() => getIdMap(statuses), [statuses]);
  const scheduleOfValueSectionMap = useMemo(() => getIdMap(scheduleOfValueSections), [scheduleOfValueSections]); // eslint-disable-line max-len

  const ourProject = useMemo(() => projectMap[projectId], [projectMap, projectId]);

  const {
    labourCost: estimatedLaborCost,
    labourHours,
    estimatedMaterialCost,
    estimatedOverheadCost,
    estimatedEquipmentCost,
    estimatedBurdenRate,
    contractValue,
    estimatedSubContractTotalValue,
    totalContractAmount: totalBaseContractValue,
    holdbackPercentage,
    enableBuildEstimate = false,
    startDate,
    endDate,
  } = ourProject ?? {};

  const averageWage = labourHours ? estimatedLaborCost / labourHours : 0;

  const projectCustomCategoryEstimates = useMemo(() => (
    customCategoryEstimates?.[projectId] ?? []
  ), [customCategoryEstimates, projectId]);

  // subcontracts selectors
  const subcontracts = useSelector((state) => state.projects.subContracts);
  const subContractUpdates = useSelector((state) => state.projects.subContractUpdates);

  const [selectedHistoryRecord, setSelectedHistoryRecord] = useState();
  const [selectedCostcode, setSelectedCostcode] = useState();
  const [selectedInvoice, setSelectedInvoice] = useState({});
  const [drawerMode, setDrawerMode] = useState(INVOICE_DRAWER_ADD_MODE);

  // subcontracts state
  const [subContractCOs, setSubContractCOs] = useState({});
  const [newItemNumbers, setNewItemNumbers] = useState({ subContractCOs: 1 });
  const [liveSummaryValues, setLiveSummaryValues] = useState([]);

  const {
    isToggled: showInvoiceDrawer,
    toggle: toggleInvoiceDrawer,
  } = useToggle();

  const projectInvoices = useMemo(() => (
    invoices
      .filter((i) => i.projectId === projectId || i.costcodeDistributions?.some(
        ({ projectId: distProjectId }) => distProjectId === projectId,
      ))
  ), [invoices, projectId]);

  const uncategorizedInvoices = useMemo(() => (
    processUncategorizedInvoices({
      invoices: projectInvoices,
      statusMap,
      vendors,
      projectId,
    })
  ), [projectInvoices, statusMap, vendors]);

  const sovSummaryValues = useMemo(() => {
    const { newLiveSummaryValue } = scheduleOfValuesData({
      projectId,
      templateId,
      totalBaseContractValue,
      holdbackPercentage,
      scheduleOfValues: scheduleOfValues ?? {},
      scheduleOfValueSectionMap,
    });

    newLiveSummaryValue.totalContractValue = newLiveSummaryValue.totalContractValue
      || contractValue;

    return newLiveSummaryValue;
  }, [
    projectId,
    templateId,
    totalBaseContractValue,
    holdbackPercentage,
    scheduleOfValues,
    scheduleOfValueSectionMap,
    contractValue,
  ]);

  useEffect(() => { // TODO: can this be turned into a memo??
    const newSubContractCOs = {};
    const maxItemNumbers = { subContractCOs: 1 };

    const newLiveSummaryValue = {
      projectId,
      totalChanges: 0,
      changeOrderProgressToDate: 0,
    };

    let values;
    let mostRecentValue;

    Object.keys(subContractUpdates).forEach((key) => {
      values = subContractUpdates[key];

      if (!values.length) return;

      mostRecentValue = values[values.length - 1];

      if (mostRecentValue.projectId !== projectId) return;

      const itemNumberInt = parseInt(mostRecentValue.itemNumber, 10);

      if (!(mostRecentValue.formId in newSubContractCOs)) {
        newSubContractCOs[mostRecentValue.formId] = {
          changes: [],
          totalChanges: 0,
        };
      }
      newSubContractCOs[mostRecentValue.formId].changes.push(mostRecentValue);
      newSubContractCOs[mostRecentValue.formId]
        .totalChanges += mostRecentValue.contractAmount;

      newLiveSummaryValue.totalChanges += mostRecentValue.contractAmount;
      newLiveSummaryValue.changeOrderProgressToDate += mostRecentValue.progressToDate;

      if (itemNumberInt >= maxItemNumbers.subContractCOs) {
        maxItemNumbers.subContractCOs = itemNumberInt + 1;
      }
    });

    setNewItemNumbers(maxItemNumbers);
    setSubContractCOs(newSubContractCOs);
    setLiveSummaryValues([newLiveSummaryValue]);
  }, [subContractUpdates, projectId]);

  const filteredSubContracts = useMemo(() => (
    subcontracts.map((subcontract) => {
      const {
        id,
        formId,
        projectId: subcontractProjectId,
        phaseId,
        costcodeId,
        children,
        contractValue: subcontractContractValue,
        costToDate,
      } = subcontract;

      const { progressHistory } = getProgressFromState({
        progressState: progresses,
        taskState: projectCostcodeDetailsMap,
        id,
        projectId: subcontractProjectId,
        phaseId,
        costcodeId,
        formId,
        children,
      });

      const {
        actualSpend = 0,
      } = progressHistory[0] || {};

      const {
        [formId]: {
          totalChanges = 0,
        } = {},
      } = subContractCOs;

      const contractTotal = subcontractContractValue + totalChanges;

      let percentageComplete = 0;
      if (contractTotal) percentageComplete = costToDate / contractTotal;

      // Sub-contract from API contains most up-to-date contract value as it is not tracked
      // by timestamp. We need to use the contract value from the progress history for
      // cost to date based on filter data.
      return {
        ...subcontract,
        costToDate: actualSpend,
        itemNumber: subcontract.number,
        description: subcontract.name,
        contractAmount: subcontract.contractValue,
        totalChanges,
        contractTotal,
        percentageComplete,
        progressToDate: subcontract.costToDate,
      };
    })
  ), [
    subcontracts,
    progresses,
    projectCostcodeDetailsMap,
    subContractCOs,
  ]);

  const filteredProjectCostUpdates = useMemo(() => {
    if (!projectProgressFilters?.dateRange) return projectCostUpdates;

    const { start, end } = projectProgressFilters?.dateRange ?? {};

    return projectCostUpdates.filter((cost) => cost.timestamp >= start && cost.timestamp <= end);
  }, [projectCostUpdates, projectProgressFilters]);

  const {
    uncategorizedMaterialUpdates,
    uncategorizedEquipmentUpdates,
    uncategorizedMaterialBudgetUpdates,
    uncategorizedEquipmentBudgetUpdates,
    categorizedProgressUpdateMap,
    categorizedBudgetUpdateMap,
  } = useMemo(() => {
    const progressUncategorized = processUncategorizedUpdates(filteredProjectCostUpdates);
    const budgetUncategorized = processUncategorizedUpdates(
      filteredProjectCostUpdates,
      { isBudgetUpdate: true },
    );
    return {
      uncategorizedMaterialUpdates: progressUncategorized.uncategorizedMaterialUpdates,
      uncategorizedEquipmentUpdates: progressUncategorized.uncategorizedEquipmentUpdates,
      uncategorizedMaterialBudgetUpdates: budgetUncategorized.uncategorizedMaterialUpdates,
      uncategorizedEquipmentBudgetUpdates: budgetUncategorized.uncategorizedEquipmentUpdates,
      categorizedProgressUpdateMap: buildCategorizedCostUpdateMap(
        filteredProjectCostUpdates,
        { isBudgetUpdate: false },
      ),
      categorizedBudgetUpdateMap: buildCategorizedCostUpdateMap(
        filteredProjectCostUpdates,
        { isBudgetUpdate: true },
      ),
    };
  }, [filteredProjectCostUpdates]);

  const { totalMaterialCostUpdates, totalEquipmentCostUpdates } = useMemo(
    () => getBudgetUpdateEstimateTotals(projectCostUpdates, {
      useBuildEstimate: !!enableBuildEstimate,
    }),
    [projectCostUpdates, enableBuildEstimate],
  );

  const materialCostTotal = (estimatedMaterialCost || 0) + totalMaterialCostUpdates;
  const equipmentCostTotal = (estimatedEquipmentCost || 0) + totalEquipmentCostUpdates;

  const computeActualSpend = useCallback(({
    phaseId,
    costcodeId,
    defaultCategoryId = null,
    customCategoryId = null,
  }) => (
    getActualSpendForCategory({
      costcodeId,
      phaseId,
      projectId,
      progresses,
      defaultCategoryId,
      customCategoryId,
    })
  ), [projectId, progresses]);

  const computeActualCostForCostCode = useCallback(({ phaseId, costcodeId }) => (
    getActualCostForCostCode({
      costcodeDetailsMap: projectCostcodeDetailsMap,
      costcodeId,
      phaseId,
      userMap,
    })
  ), [projectCostcodeDetailsMap, userMap]);

  const unphasedCostcodes = useMemo(() => (
    projectCostcodes.filter((cc) => !cc.phased)
  ), [projectCostcodes]);

  const {
    summaryData,
    costcodesTableData,
    phasesForBudget,
  } = useMemo(() => {
    const summary = getBaseSummary({
      estimatedLaborCost,
      estimatedMaterialCost: materialCostTotal,
      estimatedOverheadCost,
      estimatedEquipmentCost: equipmentCostTotal,
      estimatedBurdenRate,
      projectCustomCategoryEstimates,
    });

    const phasesData = getSummaryPhasedData({
      phases,
      projectId,
      costcodeIdMap,
      categorizedCostUpdateMap: categorizedProgressUpdateMap,
      categoryAssignments,
      categoriesIdMap,
      computeActualSpend,
      computeActualCostForCostCode,
    });

    const enrichedPhased = phasesData.flatMap((p) => p.costcodes);

    const unphasedTableData = buildCostcodesTableData(unphasedCostcodes);
    const enrichedUnphased = enrichRowsWithCostUpdates(
      unphasedTableData.map((row) => attachCostcodeCosts(
        row,
        categoriesIdMap,
        computeActualSpend,
        computeActualCostForCostCode,
      )),
      categorizedProgressUpdateMap,
    );

    const allEnrichedRows = [
      ...flattenRows(enrichedUnphased),
      ...flattenRows(enrichedPhased),
    ];

    allEnrichedRows.forEach((row) => addSummaryValues({
      row,
      summary,
      estimatedBurdenRate,
    }));

    // Compute total actual spend using the helper function
    summary.actualTotalSpend = computeActualTotalSpend({
      phases,
      costcodes: projectCostcodes,
      projectId,
      progresses,
      projectCostcodeDetailsMap,
      userMap,
      filteredSubContracts,
      uncategorizedInvoices,
      uncategorizedMaterialUpdates,
      uncategorizedEquipmentUpdates,
      estimatedBurdenRate,
      categoriesIdMap,
    });

    summary.allocatedHours = getSummaryAllocatedHours({
      unphasedCostcodes,
      phases: phasesData,
      summary,
    });

    summary.overBudgetItems = getSummaryOverBudgetItems(summary);

    const {
      uncategorizedInvoiceCosts,
      uncategorizedMaterialCosts,
      uncategorizedEquipmentCosts,
    } = getSummaryUncategorizedCosts({
      uncategorizedInvoices,
      uncategorizedMaterialUpdates,
      uncategorizedEquipmentUpdates,
    });
    summary.actualMaterialSpend += uncategorizedMaterialCosts;
    summary.actualEquipmentSpend += uncategorizedEquipmentCosts;
    summary.uncategorizedCosts = uncategorizedInvoiceCosts;

    applySubcontractSummary({
      summary,
      subcontracts: filteredSubContracts,
      estimatedSubContractTotalValue,
    });

    return {
      summaryData: [summary],
      costcodesTableData: [...enrichedUnphased, ...enrichedPhased],
      phasesForBudget: phasesData,
    };
  }, [
    phases,
    projectCostcodes,
    unphasedCostcodes,
    projectId,
    costcodeIdMap,
    projectCostcodeDetailsMap,
    userMap,
    filteredSubContracts,
    projectProgressFilters,
    computeActualSpend,
    computeActualCostForCostCode,
    estimatedLaborCost,
    estimatedOverheadCost,
    estimatedBurdenRate,
    estimatedSubContractTotalValue,
    materialCostTotal,
    equipmentCostTotal,
    uncategorizedInvoices,
    uncategorizedMaterialUpdates,
    uncategorizedEquipmentUpdates,
    categorizedProgressUpdateMap,
    categoriesIdMap,
    projectCustomCategoryEstimates,
  ]);

  const onHistoryClick = useCallback((record) => {
    const {
      id: costcodeId,
      categoryId,
      isPhaseRow,
    } = record ?? {};
    setSelectedHistoryRecord({
      ...record,
      costcodeId: isPhaseRow ? null : costcodeId,
      activeCategoryId: isPhaseRow ? null : categoryId,
      projectId,
      type: isPhaseRow ? 'phase' : null,
    });
  }, [projectId]);

  const onHistoryInvoiceClick = useCallback((invoiceId) => {
    const invoice = invoiceMap[invoiceId];
    setSelectedHistoryRecord();
    setSelectedCostcode();
    setSelectedInvoice(invoice);
    setDrawerMode(INVOICE_DRAWER_VIEW_MODE);
    toggleInvoiceDrawer();
  }, [invoiceMap, toggleInvoiceDrawer]);

  const onCloseInvoiceDrawer = useCallback(() => {
    toggleInvoiceDrawer();
    setSelectedCostcode();
    setSelectedInvoice({});
  }, [toggleInvoiceDrawer]);

  const onUploadInvoice = useCallback((record) => {
    const { id: costcodeId, phaseId } = record ?? {};
    setSelectedCostcode({ costcodeId, phaseId });
    setSelectedInvoice({});
    setDrawerMode(INVOICE_DRAWER_ADD_MODE);
    toggleInvoiceDrawer();
  }, [toggleInvoiceDrawer]);

  const onUncategorizedInvoiceClick = useCallback((invoice) => ({
    onClick: () => {
      setSelectedCostcode();
      setSelectedInvoice(invoice);
      setDrawerMode(INVOICE_DRAWER_VIEW_MODE);
      toggleInvoiceDrawer();
    },
  }), [toggleInvoiceDrawer]);

  const loadSubcontracts = useCallback(() => {
    if (!hasPOPerms) return;
    dispatch(getSubContracts(projectId));
    dispatch(getProgressSubContractUpdates(projectId, projectProgressFilters?.dateRange));
  }, [hasPOPerms, projectId, projectProgressFilters?.dateRange]);

  useEffect(() => {
    const now = DateTime.local();
    if (
      visible
      && (
        now.diff(dataTimers.progress.time, 'minutes').as('minute') > 15
        || !areFiltersEqual(projectProgressFilters?.dateRange, dataTimers.progress.filters)
      )
    ) {
      dispatch(getProgress(projectProgressFilters?.dateRange));
      dataTimers.progress.time = now;
      dataTimers.progress.filters = projectProgressFilters?.dateRange
        ? JSON.parse(JSON.stringify(projectProgressFilters?.dateRange))
        : null;
    }
  }, [visible, projectProgressFilters]);

  useEffect(() => {
    const now = DateTime.local();
    if (
      visible
      && projectId
      && (
        now.diff(dataTimers.subcontractorsAndCostcodeDetails.time, 'minutes').as('minute') > 5
        || !areFiltersEqual(projectProgressFilters?.dateRange, dataTimers.subcontractorsAndCostcodeDetails.filters) // eslint-disable-line max-len
        || dataTimers.projectId !== projectId
      )
    ) {
      dispatch(getAllProjectCostcodeDetails(projectId, projectProgressFilters?.dateRange));
      loadSubcontracts();
      dataTimers.subcontractorsAndCostcodeDetails.time = now;
      dataTimers.subcontractorsAndCostcodeDetails.filters = projectProgressFilters?.dateRange
        ? JSON.parse(JSON.stringify(projectProgressFilters?.dateRange))
        : null;
      dataTimers.projectId = projectId;
    }
  }, [visible, projectId, loadSubcontracts, projectProgressFilters]);

  useEffect(() => {
    dispatch(getCompanyImageURL());
    dispatch(updateProjectProgressFilters({ dateRange: null }));
    dispatch(getInvoices());
  }, []);

  useEffect(() => {
    dispatch(getScheduleOfValueSections({
      projectId,
      templateId,
    }));
    dispatch(getProjectScheduleOfValues({
      projectId,
      templateId,
    }));
  }, [projectId, templateId]);

  const hasUncategorizedSection = (
    uncategorizedInvoices?.length > 0
    || uncategorizedMaterialUpdates?.length > 0
    || uncategorizedEquipmentUpdates?.length > 0
    || uncategorizedMaterialBudgetUpdates?.length > 0
    || uncategorizedEquipmentBudgetUpdates?.length > 0
  );

  return (
    <>
      <div
        className="cost-tracking-scroll-wrapper"
        style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 180px)' }}
      >
        <div className="cost-tracking-layout">
          <div className="cost-tracking-sidebar">
            <CostTrackingNavigation
              hasUncategorizedSection={hasUncategorizedSection}
            />
          </div>
          <div className="cost-tracking-main" style={{ marginBottom: 100 }}>
            <CostTrackingSummaryOverview
              summaryData={summaryData}
              sovSummaryValues={sovSummaryValues}
            />
            <CostTrackingAnalysis
              onUploadInvoice={hasWrite ? onUploadInvoice : null}
              projectId={projectId}
              summaryData={summaryData}
              costcodesTableData={costcodesTableData}
              hasUncategorizedInvoices={uncategorizedInvoices?.length > 0}
              sovSummaryValues={sovSummaryValues}
              userMap={userMap}
              averageWage={averageWage}
            />
            <div id="budget-breakdown">
              <CostTrackingBudget
                divisionId={divisionId}
                costcodesTableData={costcodesTableData}
                phases={phasesForBudget}
                categorizedBudgetUpdateMap={categorizedBudgetUpdateMap}
                onUploadInvoice={hasWrite ? onUploadInvoice : null}
                onHistoryClick={onHistoryClick}
                projectId={projectId}
                averageWage={averageWage}
                useBuildEstimate={!!enableBuildEstimate}
                projectCostcodes={projectCostcodes}
                onCostcodesChanged={refreshProjectCostcodes}
              />
            </div>
            {hasPOPerms && (
              <CostTrackingSubcontracts
                subcontracts={filteredSubContracts}
                onHistoryClick={onHistoryClick}
                loadSubcontracts={loadSubcontracts}
                projectId={projectId}
                subContractCOs={subContractCOs}
                newItemNumbers={newItemNumbers}
                setNewItemNumbers={setNewItemNumbers}
                liveSummaryValues={liveSummaryValues}
              />
            )}
            {hasUncategorizedSection && (
              <CostTrackingUncategorized
                uncategorizedInvoices={uncategorizedInvoices}
                uncategorizedMaterialUpdates={uncategorizedMaterialUpdates}
                uncategorizedEquipmentUpdates={uncategorizedEquipmentUpdates}
                uncategorizedMaterialBudgetUpdates={uncategorizedMaterialBudgetUpdates}
                uncategorizedEquipmentBudgetUpdates={uncategorizedEquipmentBudgetUpdates}
                projectInvoices={projectInvoices}
                onUncategorizedClick={onUncategorizedInvoiceClick}
              />
            )}
            <CostTrackingEstimates
              projectId={projectId}
              contractValue={contractValue}
              contractTotalValue={sovSummaryValues?.totalContractValue ?? contractValue}
              estimatedLaborCost={estimatedLaborCost}
              estimatedLabourHours={labourHours}
              estimatedMaterialCost={estimatedMaterialCost}
              estimatedOverheadCost={estimatedOverheadCost}
              estimatedEquipmentCost={estimatedEquipmentCost}
              materialCostTotal={materialCostTotal}
              equipmentCostTotal={equipmentCostTotal}
              estimatedBurdenRate={estimatedBurdenRate}
              estimatedSubContractTotalValue={estimatedSubContractTotalValue}
              projectCustomCategoryEstimates={projectCustomCategoryEstimates}
              filteredSubContracts={filteredSubContracts}
              allocatedHours={summaryData?.[0]?.allocatedHours}
              allocatedColor={undefined}
              useBuildEstimate={!!enableBuildEstimate}
              startDate={startDate}
              endDate={endDate}
            />
          </div>
        </div>
      </div>
      <InvoiceAddDrawer
        invoice={selectedInvoice}
        visible={showInvoiceDrawer}
        mode={drawerMode}
        useRange={false}
        closeDrawer={onCloseInvoiceDrawer}
        updateMode={(mode) => setDrawerMode(mode)}
        projectId={projectId}
        costcode={selectedCostcode}
        onInvoiceSubmit={() => dispatch(getProgress(projectProgressFilters?.dateRange))}
        zIndex={1001}
        hasWrite={hasWrite}
        divisionId={divisionId}
      />
      <ProgressHistoryDrawer
        visible={selectedHistoryRecord && selectedHistoryRecord.id}
        record={selectedHistoryRecord}
        onClose={() => setSelectedHistoryRecord()}
        onUploadInvoice={onUploadInvoice}
        hasWrite={hasWrite}
        costcodeIdMap={costcodeIdMap}
        onInvoiceClick={onHistoryInvoiceClick}
      />
    </>
  );
}

CostTracking.propTypes = {
  visible: PropTypes.bool.isRequired,
  projectId: PropTypes.string,
  divisionId: PropTypes.string.isRequired,
  templateId: PropTypes.string,
};

CostTracking.defaultProps = {
  projectId: null,
  templateId: null,
};
